import { Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { IAnswerIndex, IApplicationFormIndex, IHttpResponse, TAnswerValue } from '@boldpenguin/sdk';
import { Actions } from 'bqx-deal';
import { LogLevel, LoggerAbstractService } from 'bqx-logging';
import { NGXLogger } from 'ngx-logger';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SDKSessionItem } from 'src/app/enums/sdk-application.enum';
import { QuoteInfo } from 'src/app/models/application-form/search-detail.model';
import { ISearchResult } from 'src/app/models/application-form/search-form.model';
import { ApplicationSearchAbstractService } from 'src/app/services/application-search/application-search.abstract.service';
import { ApplicationSearchService } from 'src/app/services/application-search/application-search.service';
import { AbstractDealAnalyticsService } from 'src/app/services/deal-analytics/abstract-deal-analytics.service';
@Component({
    selector: 'app-search-application-forms',
    templateUrl: './search-application-forms.component.html',
    styleUrls: ['./search-application-forms.component.css'],
    providers: [
        {
            provide: ApplicationSearchAbstractService,
            useClass: ApplicationSearchService
        }
    ],
    standalone: false
})
export class SearchApplicationFormsComponent implements OnInit {
  private _onjSearchResultUnSub$ = new Subject<void>();
  private ELEMENT_DATA;
  private _isSearchFormReady$ = new Observable<boolean>();
  private _areSearchLoading$ = new Observable<boolean>();
  private _isTableLoaded = false;
  private _window = window;

  public displayedColumns: string[] = [
    'mqs_reference_id',
    'mqs_first_name',
    'mqs_last_name',
    'mqs_business_name',
    'mqs_phone',
    'mqs_email',
    'mqs_route',
    'mqs_focused',
    'mqs_created_at',
    'id'
  ];
  public dataSource: MatTableDataSource<ISearchResult>;
  public pageIndex = 0;
  public pageSize = 10;
  public pageSizeOptions = [10, 25, 50];
  public matSortActive = 'mqs_reference_id';
  public matSortDirection = 'asc' as SortDirection;

  public get areSearchLoading$(): Observable<boolean> {
    return this._areSearchLoading$;
  }

  public get isTableLoaded(): boolean {
    return this._isTableLoaded;
  }
  public get isSearchFormReady$(): Observable<boolean> {
    return this._isSearchFormReady$;
  }

  public set isSearchFormReady(status: boolean) {
    this.applicationSearchService.isSearchFormReady$ = status;
    this._isSearchFormReady$ = this.applicationSearchService.isSearchFormReady$;
  }

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private applicationSearchService: ApplicationSearchAbstractService,
    private loggerService: LoggerAbstractService,
    private logger: NGXLogger,
    private router: Router,
    private dealAnalyticsService: AbstractDealAnalyticsService
  ) {}

  ngOnInit(): void {
    this._isSearchFormReady$ = this.applicationSearchService.isSearchFormReady$;
    this._areSearchLoading$ = this.applicationSearchService.areSearchLoading$;
    this.applicationSearchService.getApplicationForms();
    this.subscribeToSearchResult();
    this.dealAnalyticsService.postDealPageEvent('/search_forms', 'Search');
  }

  public viewApplication(app: QuoteInfo): void {
    sessionStorage.setItem(SDKSessionItem.UID_TOKEN, app.id);
    this.router.navigateByUrl(`/application_form?appid=${app.id}`); // Navigate to Agent UI monoline
  }

  private subscribeToSearchResult() {
    this.applicationSearchService.searchResult$.pipe(takeUntil(this._onjSearchResultUnSub$)).subscribe(
      (data: IHttpResponse<IApplicationFormIndex[]>) => {
        this._isTableLoaded = true;
        this.updateDataSource(data.parsedBody);
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'application-form: subscribeToSearchResult', err, '');
        this._isTableLoaded = false;
        this._onjSearchResultUnSub$.next();
        this._onjSearchResultUnSub$.complete();
      }
    );
  }
  private updateDataSource(dataArray: IApplicationFormIndex[]): void {
    this.ELEMENT_DATA = [];
    dataArray.forEach((data: IApplicationFormIndex) => {
      const dataRow = {
        mqs_reference_id: data.answers.find((ans: IAnswerIndex<TAnswerValue>) => ans.question.code === 'mqs_reference_id')?.value,
        mqs_first_name: data.answers.find((ans: IAnswerIndex<TAnswerValue>) => ans.question.code === 'mqs_first_name')?.value,
        mqs_last_name: data.answers.find((ans: IAnswerIndex<TAnswerValue>) => ans.question.code === 'mqs_last_name')?.value,
        mqs_business_name: data.answers.find((ans: IAnswerIndex<TAnswerValue>) => ans.question.code === 'mqs_business_name')?.value,
        mqs_phone: data.answers.find((ans: IAnswerIndex<TAnswerValue>) => ans.question.code === 'mqs_phone')?.value,
        mqs_email: data.answers.find((ans: IAnswerIndex<TAnswerValue>) => ans.question.code === 'mqs_email')?.value,
        mqs_route: data.answers.find((ans: IAnswerIndex<TAnswerValue>) => ans.question.code === 'mqs_route')?.value,
        mqs_focused: data.answers.find((ans: IAnswerIndex<TAnswerValue>) => ans.question.code === 'mqs_focused')?.value,
        mqs_created_at: data.created_at,
        id: data.id
      };
      this.ELEMENT_DATA.push(dataRow);
    });
    this.dataSource = new MatTableDataSource<ISearchResult>(this.ELEMENT_DATA);
    this.dataSource.paginator = this.paginator;
  }
  public onSearchChanged(sort?: Sort): Sort {
    this.applicationSearchService.sort = sort;
    this.applicationSearchService.getApplicationForms();
    return sort;
  }

  public onPageSelect(event?: PageEvent): PageEvent {
    this.applicationSearchService.page = event;
    this.applicationSearchService.getApplicationForms();
    return event;
  }

  public newOnClick() {
    this.dealAnalyticsService.postDealControlEvent(Actions.ButtonClick, 'New', '/search_forms', 'Search');
  }
}
